import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';

import { Asset } from '../components/models/asset.model';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { PaymentService } from '../services/payment.service';
import { tapResponse } from '@ngrx/operators';
import { lastValueFrom, pipe, switchMap } from 'rxjs';
import { UserService } from '../services/user.service';

type AssetStoreState = {
  loaded: { assets: boolean; userAcceptedAssets: boolean };
  selectedAsset: string | null;
  assets: Asset[];
  userAcceptedAssets: Asset[];
};

const initialState: AssetStoreState = {
  loaded: { assets: false, userAcceptedAssets: false },
  selectedAsset: null,
  assets: [],
  userAcceptedAssets: [],
};

export const AssetStore = signalStore(
  {
    providedIn: 'root',
  },
  withEntities<Asset>(),
  withState(initialState),
  withComputed(({ entities, selectedAsset, userAcceptedAssets }) => {
    const decimalPlacesFor = computed(() => {
      return entities().reduce((acc: Record<string, number>, asset: Asset) => {
        acc[asset.assetTicker] = asset.decimalPrecisionUI;
        return acc;
      }, {});
    });

    const getSelectedAsset = computed(() => {
      const asset = selectedAsset();
      if (asset) {
        return entities()[asset as any];
      } else {
        return null;
      }
    });

    const acceptedAssetsIds = computed(() => {
      return userAcceptedAssets().map((asset) => asset.id);
    });

    const availableNetworks = computed(() => {
      const networks: string[] = [];
      entities().forEach((asset) => {
        if (!networks.includes(asset.assetChain) && !asset.isFiat) {
          networks.push(asset.assetChain);
        }
      });
      return networks;
    });

    const getConnectedAssets = computed(() => {
      return entities()
        .filter((asset) => !(asset.isMultiChain && asset.assetChain != 'ETH'))
        .map((asset) => ({ ...asset, enabled: acceptedAssetsIds().includes(asset.id) }));
    });

    const currencies = computed(() => {
      return getConnectedAssets()
        .filter((asset) => asset.isFiat)
        .sort((a, b) => a.id.localeCompare(b.id));
    });

    const stablecoins = computed(() => {
      return getConnectedAssets()
        .filter((asset) => !asset.isFiat && ['USDC', 'USDT'].includes(asset.assetTicker))
        .sort((a, b) => a.id.localeCompare(b.id));
    });

    const cryptoassets = computed(() => {
      return getConnectedAssets()
        .filter((asset) => !asset.isFiat && !['USDC', 'USDT'].includes(asset.assetTicker))
        .sort((a, b) => a.id.localeCompare(b.id));
    });

    return {
      currencies,
      stablecoins,
      cryptoassets,
      availableNetworks,
      decimalPlacesFor,
      getSelectedAsset,
      getConnectedAssets,
    };
  }),
  withMethods((store, paymentService = inject(PaymentService), userService = inject(UserService)) => {
    async function loadAssets(): Promise<any> {
      try {
        const assets = await lastValueFrom(paymentService.getAllAssets());

        const sortedAssets = assets.sort((a, b) => {
          if (a.isFiat && !b.isFiat) return -1;
          if (!a.isFiat && b.isFiat) return 1;
          return a.assetTicker.localeCompare(b.assetTicker);
        });

        patchState(store, setAllEntities(sortedAssets));
        patchState(store, {
          loaded: { ...store.loaded(), assets: true },
        });

        return assets;
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    const loadUserAcceptedAssets = rxMethod<void>(
      pipe(
        switchMap(() => {
          return userService.getAcceptedAssets().pipe(
            tapResponse({
              next: (userAcceptedAssets) => {
                patchState(store, { userAcceptedAssets: userAcceptedAssets });
                patchState(store, {
                  loaded: { ...store.loaded(), userAcceptedAssets: true },
                });
              },
              error: (error: { message: string }) => {
                // todo: handle error
                console.log(error);
              },
            }),
          );
        }),
      ),
    );

    const selectAsset = (assetTicker: string) => {
      patchState(store, { selectedAsset: assetTicker });
    };

    const setAcceptedAssets = rxMethod<Asset[]>(
      pipe(
        switchMap((assets: Asset[]) => {
          return userService.setAcceptedAssets(assets.map((asset) => asset.id)).pipe(
            tapResponse({
              next: () => {
                patchState(store, { userAcceptedAssets: assets });
              },
              error: (error: { message: string }) => {
                console.error('Error setting accepted assets:', error);
              },
            }),
          );
        }),
      ),
    );

    return {
      loadAssets,
      loadUserAcceptedAssets,
      selectAsset,
      setAcceptedAssets,
    };
  }),
  withHooks({
    onInit(store) {
      store.loadAssets();
    },
  }),
);
